import {
  ServicePageModalType,
  useServicePageModalContext,
} from 'restaurantAdmin/context/ServicePageModalContext';
import { useReservationServiceContext } from 'restaurantAdmin/reservations/service/state/ReservationServiceContext';
import { Dialog } from '@base-ui/react';
import { Button, ButtonVariants } from '@components/button/Button';
import { errorToast } from '@components/toasts/Toasts';
import { reportAppError } from '@shared/reportAppError';
import { getFullName } from '@shared/utils/formatName';
import { isWalkIn } from '../apiHelpers';
import { useServiceSearchParams } from '../hooks/useServiceSearchParams';
import { SeatingSelectedTables } from '../SeatingSelectedTables';
import { HeaderBar } from '../sidePanel/HeaderBar';
import { useReservationServiceSelectedSeatingContext } from '../state/ReservationServiceSelectedSeatingContext';
import { useChangeWalkInSeat, useWalkInAvailabilities } from './hooks';
import styles from './SeatWalkInFormDrawer.scss';

export const ChangeWalkInSeatDrawer = () => {
  const { closeSidePanelSheet, selectedOccupant } =
    useReservationServiceContext();
  const { openModal } = useServicePageModalContext();
  const { selectedFloorPlanTables } =
    useReservationServiceSelectedSeatingContext();
  const { closeDrawer } = useServiceSearchParams();

  const { data: walkInAvailabilities, error: walkInAvailabilityFetchError } =
    useWalkInAvailabilities({
      floorPlanTables: selectedFloorPlanTables,
      guestCount: selectedOccupant?.guestCount,
    });

  const changeWalkInSeatMutation = useChangeWalkInSeat({
    onSuccess: () => {
      closeSidePanelSheet();
      closeDrawer();
    },
  });

  const onChangeWalkInSeating = async () => {
    if (walkInAvailabilityFetchError) {
      errorToast({
        message: 'Failed to confirm seat availability. Please try again.',
      });
      reportAppError(walkInAvailabilityFetchError);
      return;
    }

    if (!selectedOccupant) return;

    if (walkInAvailabilities.some((a) => !a.hasAvailability)) {
      openModal(ServicePageModalType.WalkInSeatingConflict, {
        handleConfirm: () => {
          void changeWalkInSeatMutation.mutate({
            walkInId: selectedOccupant.id,
            floorPlanTables: selectedFloorPlanTables,
          });
        },
      });
      return;
    }

    changeWalkInSeatMutation.mutate({
      walkInId: selectedOccupant.id,
      floorPlanTables: selectedFloorPlanTables,
    });
  };

  if (!isWalkIn(selectedOccupant)) {
    return null;
  }

  const walkInName =
    getFullName(selectedOccupant.firstName, selectedOccupant.lastName) ||
    'Walk-In';

  return (
    <div className={styles.container}>
      <HeaderBar
        name={walkInName}
        guestCount={selectedOccupant.guestCount}
        time={selectedOccupant.time}
      />

      <div className={styles.drawerContent}>
        <SeatingSelectedTables floorPlanTables={selectedFloorPlanTables} />
        <div className={styles.callToActionSection}>
          <Button
            label="Seat Walk-in"
            type="button"
            onClick={onChangeWalkInSeating}
            variant={ButtonVariants.Primary}
            isDisabled={selectedFloorPlanTables.length === 0}
          />

          <Dialog.Close
            render={() => (
              <Button
                label="Cancel"
                onClick={closeDrawer}
                variant={ButtonVariants.Tertiary}
              />
            )}
          />
        </div>
      </div>
    </div>
  );
};
